import React, { useState, useEffect } from 'react';
import { configAPI } from '../../api';
import { Card, Button, Input, Select, Spinner } from '../../components/ui';
import { Plus, X } from 'lucide-react';

export const GamificationConfigPage = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState(null);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await configAPI.get();
        setConfig(response.data);
        setEditForm(response.data);
      } catch (err) {
        setEditForm({
          point_rules: [],
          badges: [],
          levels: [],
          settings: { confirmation_window_hours: 48, leaderboard_privacy: 'top_10', streak_reset_days: 3 }
        });
      } finally {
        setLoading(false);
      }
    };
    loadConfig();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await configAPI.create(editForm);
      setConfig(response.data);
      alert('Configuration saved successfully!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateRule = (index, field, value) => {
    const newRules = [...editForm.point_rules];
    newRules[index] = { ...newRules[index], [field]: value };
    setEditForm({ ...editForm, point_rules: newRules });
  };

  const addRule = () => {
    setEditForm({
      ...editForm,
      point_rules: [...editForm.point_rules, {
        id: crypto.randomUUID(),
        name: 'New Rule',
        event_type: 'SESSION_LOGGED',
        points: 10,
        description: '',
        max_per_day: null,
        is_active: true
      }]
    });
  };

  const removeRule = (index) => {
    const newRules = editForm.point_rules.filter((_, i) => i !== index);
    setEditForm({ ...editForm, point_rules: newRules });
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="gamification-config-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Gamification Config</h1>
          <p className="text-gray-500">
            Configure points, badges, and rules
            {config && <span className="ml-2 text-xs bg-gray-200 px-2 py-0.5 rounded">v{config.version}</span>}
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Spinner size="sm" /> : 'Save Configuration'}
        </Button>
      </div>

      {/* Settings */}
      <Card className="p-6">
        <h3 className="font-semibold mb-4">General Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Confirmation Window (hours)"
            type="number"
            value={editForm?.settings?.confirmation_window_hours || 48}
            onChange={e => setEditForm({
              ...editForm,
              settings: { ...editForm.settings, confirmation_window_hours: parseInt(e.target.value) }
            })}
          />
          <Input
            label="Streak Reset (days)"
            type="number"
            value={editForm?.settings?.streak_reset_days || 3}
            onChange={e => setEditForm({
              ...editForm,
              settings: { ...editForm.settings, streak_reset_days: parseInt(e.target.value) }
            })}
          />
          <Select
            label="Leaderboard Privacy"
            value={editForm?.settings?.leaderboard_privacy || 'top_10'}
            onChange={e => setEditForm({
              ...editForm,
              settings: { ...editForm.settings, leaderboard_privacy: e.target.value }
            })}
            options={[
              { value: 'public', label: 'Show All Names' },
              { value: 'top_10', label: 'Top 10 Only' },
              { value: 'alias', label: 'Use Aliases' },
            ]}
          />
        </div>
      </Card>

      {/* Point Rules */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Point Rules</h3>
          <Button size="sm" variant="outline" onClick={addRule}>
            <Plus className="w-4 h-4 mr-1" /> Add Rule
          </Button>
        </div>
        <div className="space-y-4">
          {editForm?.point_rules?.map((rule, idx) => (
            <div key={rule.id} className="p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Input
                  label="Name"
                  value={rule.name}
                  onChange={e => updateRule(idx, 'name', e.target.value)}
                />
                <Select
                  label="Event Type"
                  value={rule.event_type}
                  onChange={e => updateRule(idx, 'event_type', e.target.value)}
                  options={[
                    { value: 'SESSION_LOGGED', label: 'Session Logged' },
                    { value: 'SESSION_CONFIRMED', label: 'Session Confirmed' },
                    { value: 'PR_RECORDED', label: 'PR Recorded' },
                    { value: 'STREAK_ACHIEVED', label: 'Streak Achieved' },
                    { value: 'CHALLENGE_COMPLETED', label: 'Challenge Completed' },
                  ]}
                />
                <Input
                  label="Points"
                  type="number"
                  value={rule.points}
                  onChange={e => updateRule(idx, 'points', parseInt(e.target.value))}
                />
                <Input
                  label="Max per Day"
                  type="number"
                  value={rule.max_per_day || ''}
                  onChange={e => updateRule(idx, 'max_per_day', e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="Unlimited"
                />
              </div>
              <div className="flex items-center justify-between mt-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rule.is_active}
                    onChange={e => updateRule(idx, 'is_active', e.target.checked)}
                    className="rounded border-gray-300 text-primary-600"
                  />
                  <span className="text-sm">Active</span>
                </label>
                <Button size="sm" variant="ghost" onClick={() => removeRule(idx)}>
                  <X className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Levels */}
      <Card className="p-6">
        <h3 className="font-semibold mb-4">Levels</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2">Level</th>
                <th className="text-left py-2">Name</th>
                <th className="text-left py-2">XP Required</th>
              </tr>
            </thead>
            <tbody>
              {editForm?.levels?.map((level, idx) => (
                <tr key={level.level} className="border-b">
                  <td className="py-2">{level.level}</td>
                  <td className="py-2">
                    <input
                      className="input py-1"
                      value={level.name}
                      onChange={e => {
                        const newLevels = [...editForm.levels];
                        newLevels[idx] = { ...newLevels[idx], name: e.target.value };
                        setEditForm({ ...editForm, levels: newLevels });
                      }}
                    />
                  </td>
                  <td className="py-2">
                    <input
                      className="input py-1 w-24"
                      type="number"
                      value={level.xp_required}
                      onChange={e => {
                        const newLevels = [...editForm.levels];
                        newLevels[idx] = { ...newLevels[idx], xp_required: parseInt(e.target.value) };
                        setEditForm({ ...editForm, levels: newLevels });
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default GamificationConfigPage;
